import type { ProjectTheme } from '@octopus-do/octopus-project-core'
import { ThemeColors } from '@octopus-do/octopus-storybook'
import { useProjectLegacyActions } from '@store/migrationActions/useProjectLegacyActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type React from 'react'
import { useSelector } from 'react-redux'

import {
  THEME_BLUEPRINT,
  THEME_BOLD,
  THEME_DARK,
  THEME_LIGHT,
} from '@/constants/constants'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { clsJoin } from '@/utils'
import { Selector } from '../../../Selector'
import itemCss from '../../SettingsItem.module.scss'
import { IconBlueprint, IconBold, IconDark, IconLight } from './icons'
import css from './Theme.module.scss'

type Props = {
  inline?: boolean
  disabled?: boolean
}

const THEMES = [
  {
    id: THEME_LIGHT as ProjectTheme,
    title: 'Light',
    className: clsJoin(ThemeColors.light, css.theme_selector, css.light),
    icon: <IconLight />,
  },
  {
    id: THEME_DARK as ProjectTheme,
    title: 'Dark',
    className: clsJoin(ThemeColors.dark, css.theme_selector),
    icon: <IconDark />,
  },
  {
    id: THEME_BLUEPRINT as ProjectTheme,
    title: 'Blueprint',
    className: clsJoin(ThemeColors.blueprint, css.theme_selector),
    icon: <IconBlueprint />,
  },
  {
    id: THEME_BOLD as ProjectTheme,
    title: 'Bold',
    className: clsJoin(ThemeColors.bold, css.theme_selector),
    titleClassName: css.bold_title,
    icon: <IconBold />,
  },
]

const Theme: React.FC<Props> = ({ inline = false, disabled = false }) => {
  const theme = useSelector(migrationProjectSelector.getProjectTheme)

  const { changeProjectTheme } = useProjectLegacyActions()

  const onClick = (theme: ProjectTheme) => {
    changeProjectTheme({ theme })

    sendGtmEvent(
      GtmTriggerName.PROJECT_THEME,
      'CLick to change theme from sidebar'
    )
  }

  return (
    <div className={clsJoin(itemCss.container, inline && itemCss.inline)}>
      <div className={itemCss.titleWrapper}>Theme</div>

      <div className={itemCss.selectors}>
        {THEMES.map(({ id, title, icon, className, titleClassName }) => (
          <Selector
            key={id}
            active={theme === id}
            disabled={disabled}
            background
            title={title}
            classnames={{
              container: className,
              title: titleClassName,
            }}
            icon={icon}
            onClick={() => onClick(id)}
          />
        ))}
      </div>
    </div>
  )
}

export default Theme
